/** Team API calls (typed). */

import {
  apiDelete,
  apiGet,
  apiPatch,
  apiPost,
  apiPut,
  type PaginatedResponse,
  type QueryParams,
} from "@/lib/api-client";
import type {
  AccessAction,
  ApiMemberDocument,
  ApiTeamMember,
  InvitationResult,
  MemberAccess,
  PermissionException,
  PermissionExceptionInput,
} from "./team.adapter";

export interface TeamListParams {
  page?: number;
  limit?: number;
  search?: string;
  role?: string;
  status?: string;
  sortBy?: string;
  sortOrder?: "asc" | "desc";
  archived?: boolean;
}

export const teamApi = {
  list: (params: TeamListParams = {}) =>
    apiGet<PaginatedResponse<ApiTeamMember>>("/team", params as QueryParams),
  get: (id: string) => apiGet<ApiTeamMember>(`/team/${id}`),
  me: () => apiGet<ApiTeamMember>("/team/me"),
  create: (body: Record<string, unknown>) => apiPost<ApiTeamMember>("/team", body),
  update: (id: string, body: Record<string, unknown>) =>
    apiPatch<ApiTeamMember>(`/team/${id}`, body),
  archive: (id: string) => apiDelete<ApiTeamMember>(`/team/${id}`),
  restore: (id: string) => apiPost<ApiTeamMember>(`/team/${id}/restore`),

  // ── Skills ──
  addSkill: (id: string, body: Record<string, unknown>) => apiPost(`/team/${id}/skills`, body),
  removeSkill: (id: string, skillId: string) => apiDelete(`/team/${id}/skills/${skillId}`),

  // ── Documents ──
  listDocuments: (id: string) => apiGet<ApiMemberDocument[]>(`/team/${id}/documents`),
  addDocument: (id: string, body: Record<string, unknown>) =>
    apiPost<ApiMemberDocument>(`/team/${id}/documents`, body),
  updateDocument: (id: string, docId: string, body: Record<string, unknown>) =>
    apiPatch<ApiMemberDocument>(`/team/${id}/documents/${docId}`, body),
  removeDocument: (id: string, docId: string) => apiDelete(`/team/${id}/documents/${docId}`),

  // ── Project assignments ──
  assignProject: (id: string, body: Record<string, unknown>) =>
    apiPost(`/team/${id}/projects`, body),
  unassignProject: (id: string, projectId: string) =>
    apiDelete(`/team/${id}/projects/${projectId}`),

  // ── Access ──
  getAccess: (id: string) => apiGet<MemberAccess>(`/team/${id}/access`),
  sendInvitation: (id: string) => apiPost<InvitationResult>(`/team/${id}/access/invitation`),
  accessAction: (id: string, action: AccessAction) => apiPost(`/team/${id}/access/${action}`, {}),
  resetPassword: (id: string, body: Record<string, unknown>) =>
    apiPost(`/team/${id}/access/reset-password`, body),

  // ── Exceptions de permissions (spec RBAC v2 §3) — indexées par userId ──
  listPermissionExceptions: (userId: string) =>
    apiGet<PermissionException[]>(`/users/${userId}/permission-exceptions`),
  /** PUT = remplacement intégral de la liste. */
  replacePermissionExceptions: (userId: string, exceptions: PermissionExceptionInput[]) =>
    apiPut<PermissionException[]>(`/users/${userId}/permission-exceptions`, { exceptions }),
};
